import { Button } from '@ksyos/design-system';
import { values } from 'lodash';
import { useState } from 'react';
import type { IAdminProcess } from '@frontend/components/interfaces/adminProcessesList';
import { formatString } from '@common/utils/formatString';
import { AbortProcessReasons } from '../../../../common/enums/abortProcessReasons';
import { RequestStatus } from '../../../../common/enums/requestStatus';
import { ServiceCode } from '../../../../common/enums/serviceCode';
import { requestStatusCancelOrAbortSchema } from '../../../../common/validation/request';
import { fetchJson } from '../../../store/fetch';
import { getSelectedLanguage } from '../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { FormGroup } from '../../elements/forms/FormGroup';
import { FormLabel } from '../../elements/forms/FormLabel';
import { Modal } from '../../elements/modals/Modal';
import { TypeaheadInput } from '../../fields/InputTypeahead';

type AbortProcessModalProps = {
    adminProcess: IAdminProcess;
    onClose: () => Promise<void>;
    onConfirm: (message?: string) => Promise<void>;
    succeedingConsultation?: IAdminProcess;
};

export function AbortProcessModal(props: AbortProcessModalProps) {
    const [validationErrors, setValidationErrors] = useState(emptyValidationErrors);
    const [isAborting, setIsAborting] = useState(false);
    const [reasonStopProccess, setReasonStopProccess] = useState<string | null>(null);

    const translate = getSelectedLanguage();

    return (
        <Modal
            actionButtons={[
                <Button
                    isDisabled={!reasonStopProccess || isAborting}
                    key="abortProcess"
                    onClick={abortProcess}
                    variant="solid"
                >
                    {translate.request.confirmabortProcess}
                </Button>,
            ]}
            isOpen
            onClose={props.onClose}
            size="medium"
            title={translate.request.abortProcess}
        >
            <FormGroup hasErrors={validationErrors.has('reasonStopProccess')}>
                <FormLabel stacked>{translate.request.abortProcessConfirmation}</FormLabel>
                <TypeaheadInput
                    error={validationErrors.get('reasonStopProccess')}
                    onChange={(value) => setReasonStopProccess(value)}
                    onDoValidate={async (value) =>
                        setValidationErrors(
                            updateValidationErrorsForOne(
                                requestStatusCancelOrAbortSchema,
                                { reasonStopProccess: value },
                                validationErrors,
                                'reasonStopProccess'
                            )
                        )
                    }
                    options={getProcessAbortReasons()}
                    placeholder={translate.request.abortProcessMessage}
                    value={reasonStopProccess}
                />
            </FormGroup>
        </Modal>
    );

    async function abortProcess() {
        setIsAborting(true);

        await updateRequestStatus(props.adminProcess);

        let message = await getFinancialRecordsMessage(props.adminProcess);

        if (props.succeedingConsultation) {
            await updateRequestStatus(props.succeedingConsultation);

            const messageForSucceedingProcess = await getFinancialRecordsMessage(props.succeedingConsultation);

            if (messageForSucceedingProcess) {
                if (message) {
                    message += ' ' + messageForSucceedingProcess;
                } else {
                    message = messageForSucceedingProcess;
                }
            }
        }

        setIsAborting(false);

        await props.onConfirm(message);
    }

    async function updateRequestStatus(process: IAdminProcess) {
        const { errors, item } = validate(requestStatusCancelOrAbortSchema, {
            reasonStopProccess,
        });

        setValidationErrors(errors);

        if (errors.size > 0) {
            setIsAborting(false);

            return;
        }

        let urlRoute;

        if (
            process.status === RequestStatus.draft ||
            (process.status === RequestStatus.requested && process.service.code !== ServiceCode.GGZBtherTreat) ||
            (process.status === RequestStatus.accepted && !process.procedure)
        ) {
            urlRoute = 'cancel';
        } else {
            urlRoute = 'abort';
        }

        const url = `/api/requests/${process.id}/${urlRoute}`;

        await fetchJson(url, 'POST', item);
    }

    async function getFinancialRecordsMessage(process: IAdminProcess) {
        const { payouts, declarations, invoices } = await getExportedFinancialRecords(process.processId);

        if (payouts.length === 0 && declarations.length === 0 && invoices.length === 0) {
            return;
        }

        const andPayoutsMessage =
            payouts.length > 0 ? ` ${translate.common.and} ${translate.request.payouts} ${payouts[0].runNumber}.` : '.';

        let message = formatString(translate.request.exportedRecords, [process.processId]);

        if (declarations.length > 0) {
            message += ` ${translate.request.declarations} ${declarations[0].runNumber}${andPayoutsMessage}`;
        } else if (invoices.length > 0) {
            message += ` ${translate.request.invoices} ${invoices[0].runNumber}${andPayoutsMessage}`;
        } else {
            message += ` ${translate.request.payouts} ${payouts[0].runNumber}.`;
        }

        return `${message} ${translate.request.contactAdmin}`;
    }

    async function getExportedFinancialRecords(processId: string) {
        const url = `/api/requests/${processId}/financialRecords`;
        const result = await fetchJson(url, 'GET');

        return result.json;
    }

    function getProcessAbortReasons() {
        return values(AbortProcessReasons)
            .map((value) => translate.abortProcessReasonsEnum[value])
            .sort();
    }
}
